/**
 * Charm Builder — Studio Beige / Prysm
 * Shopify Broadcast theme compatible
 * v3 — dual-arc, rotation, material filtering, chain animation
 */

class CharmBuilder {
  constructor(el) {
    this.el = el;
    this.sectionId = el.id.replace('charm-builder-', '');
    this.baseVariantId = parseInt(el.dataset.baseVariant, 10);
    this.basePrice = parseInt(el.dataset.basePrice, 10);
    this.currencyCode = el.dataset.currency || 'CAD';

    // Charm catalogue
    const dataEl = document.getElementById(`charm-data-${this.sectionId}`);
    this.charms = dataEl ? JSON.parse(dataEl.textContent) : [];

    // Slot layout config — [{x, y, type}] from product metafield or section settings.
    // Dual-arc: 5 entries type="connector" + 5 entries type="hanging".
    // Legacy: all entries type="any".
    const cfgEl = document.getElementById(`slot-config-${this.sectionId}`);
    this.slotConfig = cfgEl ? JSON.parse(cfgEl.textContent) : [];

    // Split into connector arc and hanging arc, sort left→right for correct interpolation
    this.connectorArc = this.slotConfig.filter(s => s.type === 'connector').sort((a, b) => a.x - b.x);
    this.hangingArc   = this.slotConfig.filter(s => s.type === 'hanging').sort((a, b) => a.x - b.x);

    // Legacy fallback: type="any" or empty
    if (!this.connectorArc.length && !this.hangingArc.length) {
      const sorted = [...this.slotConfig].sort((a, b) => a.x - b.x);
      this.connectorArc = sorted;
      this.hangingArc   = sorted;
    } else if (!this.hangingArc.length) {
      this.hangingArc = this.connectorArc;
    } else if (!this.connectorArc.length) {
      this.connectorArc = this.hangingArc;
    }

    this.numSlots  = Math.max(this.connectorArc.length, this.hangingArc.length, 5);
    this.baselineY = this.connectorArc.length
      ? this.connectorArc.reduce((s, c) => s + c.y, 0) / this.connectorArc.length
      : 65;

    // Base product variants
    const baseVarEl = document.getElementById(`base-variants-${this.sectionId}`);
    this.baseVariants   = baseVarEl ? JSON.parse(baseVarEl.textContent) : [];
    this.selectedOptions = {};

    // State
    this.slots          = {};
    this.activeSlot     = 1;
    this.selectedCharm  = null;
    this.selectedVariant = null;
    this.activeFilter   = 'all';
    this.activeMaterial = 'all'; // 'gold' | 'silver' | 'all'

    this._init();
  }

  // ─── INIT ────────────────────────────────────────────────────────────────

  _init() {
    this._renderHotspots();
    this._renderFilters();
    this._repositionAllOverlays();
    this._renderGrid();
    this._bindEvents();
    this._openSlot(1);
    this._updateCartBar();
  }

  // ─── FILTER TABS ─────────────────────────────────────────────────────────

  _renderFilters() {
    const container = document.getElementById(`charm-filters-${this.sectionId}`);
    if (!container) return;

    const categories = [...new Set(this.charms.map(c => c.category).filter(Boolean))].sort();

    container.innerHTML = [
      `<button class="charm-builder__filter active" data-filter="all">All</button>`,
      ...categories.map(cat =>
        `<button class="charm-builder__filter" data-filter="${cat}">${cat}</button>`
      )
    ].join('');

    container.querySelectorAll('.charm-builder__filter').forEach(btn => {
      btn.addEventListener('click', () => {
        container.querySelectorAll('.charm-builder__filter').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.activeFilter = btn.dataset.filter;
        this._renderGrid();
      });
    });
  }

  // ─── HOTSPOT RENDER ───────────────────────────────────────────────────────
  // Hotspot rings and overlay images are SEPARATE elements inside .charm-builder__overlays.
  // Rings follow the connector arc. Overlay images follow their type-specific arc + rotation.

  _renderHotspots() {
    const container = document.getElementById(`charm-overlays-${this.sectionId}`);
    if (!container) return;

    // Hotspot rings (click targets), positioned on connector arc
    const hotspotHTML = Array.from({ length: this.numSlots }, (_, i) => {
      const num = i + 1;
      const pos = this.connectorArc[i] || { x: 50, y: this.baselineY };
      return `
        <div
          class="charm-builder__hotspot"
          data-slot="${num}"
          style="left:${pos.x}%;top:${pos.y}%;"
          role="button"
          aria-label="Charm position ${num}"
          tabindex="0"
        >
          <span class="charm-builder__hotspot-ring" data-slot="${num}"></span>
        </div>
      `;
    }).join('');

    // Overlay images (separate, positioned independently via JS)
    const overlayHTML = Array.from({ length: this.numSlots }, (_, i) => {
      const num = i + 1;
      return `<img
        class="charm-builder__overlay-charm"
        data-slot="${num}"
        src="" alt=""
        style="display:none;position:absolute;"
      >`;
    }).join('');

    container.innerHTML = hotspotHTML + overlayHTML;

    // Bind hotspot clicks
    container.querySelectorAll('.charm-builder__hotspot').forEach(spot => {
      spot.addEventListener('click', () => this._openSlot(parseInt(spot.dataset.slot, 10)));
      spot.addEventListener('keydown', e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this._openSlot(parseInt(spot.dataset.slot, 10));
        }
      });
    });
  }

  // ─── SLOT MANAGEMENT ─────────────────────────────────────────────────────

  _openSlot(slotNum) {
    this.activeSlot = slotNum;
    this.selectedCharm = null;

    this.el.querySelectorAll('.charm-builder__slot-btn').forEach(btn =>
      btn.classList.toggle('is-active', parseInt(btn.dataset.slot, 10) === slotNum)
    );
    this.el.querySelectorAll('.charm-builder__hotspot-ring').forEach(ring =>
      ring.classList.toggle('is-active', parseInt(ring.dataset.slot, 10) === slotNum)
    );

    const label = this.el.querySelector('.charm-builder__active-slot-num');
    if (label) label.textContent = slotNum;

    this.activeFilter = 'all';
    const filterContainer = document.getElementById(`charm-filters-${this.sectionId}`);
    if (filterContainer) {
      filterContainer.querySelectorAll('.charm-builder__filter').forEach(btn =>
        btn.classList.toggle('active', btn.dataset.filter === 'all')
      );
    }

    this._hideDetail();
    this._renderGrid();
  }

  _placeCharm(charm) {
    if (!charm || !this.activeSlot) return;

    const variant = this.selectedVariant;
    const slotCharm = variant
      ? { ...charm, id: variant.id, price: variant.price, image: variant.image, overlayImage: variant.overlayImage, variantTitle: variant.title }
      : charm;

    this.slots[this.activeSlot] = slotCharm;

    const slotEl = this.el.querySelector(`.charm-builder__slot[data-slot="${this.activeSlot}"]`);
    if (slotEl) {
      const thumb = slotEl.querySelector('.charm-builder__slot-thumb');
      const plus  = slotEl.querySelector('.charm-builder__slot-plus');
      const rem   = slotEl.querySelector('.charm-builder__slot-remove');
      if (thumb) { thumb.src = slotCharm.image; thumb.style.display = 'block'; }
      if (plus)  plus.style.display  = 'none';
      if (rem)   rem.style.display   = 'flex';
      slotEl.querySelector('.charm-builder__slot-btn')?.classList.add('is-filled');
    }

    this._updateOverlay(this.activeSlot, slotCharm);
    this._jiggleChain(this.activeSlot);
    this._refreshDraggable();
    this._updateCartBar();
    this._renderGrid();

    const next = this._nextEmptySlot();
    if (next) this._openSlot(next);
  }

  _removeCharm(slotNum) {
    delete this.slots[slotNum];

    const slotEl = this.el.querySelector(`.charm-builder__slot[data-slot="${slotNum}"]`);
    if (slotEl) {
      const thumb = slotEl.querySelector('.charm-builder__slot-thumb');
      const plus  = slotEl.querySelector('.charm-builder__slot-plus');
      const rem   = slotEl.querySelector('.charm-builder__slot-remove');
      if (thumb) { thumb.src = ''; thumb.style.display = 'none'; }
      if (plus)  plus.style.display  = 'flex';
      if (rem)   rem.style.display   = 'none';
      slotEl.querySelector('.charm-builder__slot-btn')?.classList.remove('is-filled', 'is-active');
    }

    const overlay = this.el.querySelector(`.charm-builder__overlay-charm[data-slot="${slotNum}"]`);
    if (overlay) {
      overlay.src = '';
      overlay.style.display = 'none';
      overlay.className = 'charm-builder__overlay-charm';
      overlay.style.transform = '';
    }

    this._jiggleChain(slotNum);
    this._refreshDraggable();
    this._updateCartBar();
    this._renderGrid();
    this._repositionAllOverlays();
    this._openSlot(slotNum);
  }

  _nextEmptySlot() {
    for (let i = 1; i <= this.numSlots; i++) {
      if (!this.slots[i]) return i;
    }
    return null;
  }

  // ─── CHARM SELECTION ─────────────────────────────────────────────────────

  _selectCharm(charm) {
    this.selectedCharm = charm;
    // Pre-select the variant matching the current material (Gold Filled / Sterling Silver)
    const matVariant = this._getVariantForMaterial(charm, this.activeMaterial);
    this.selectedVariant = matVariant || (charm.variants?.length > 0 ? charm.variants[0] : null);

    this.el.querySelectorAll('.charm-builder__charm-item').forEach(item =>
      item.classList.toggle('is-selected', parseInt(item.dataset.charmId, 10) === charm.productId)
    );

    const detail = document.getElementById(`charm-detail-${this.sectionId}`);
    if (!detail) return;

    this._updateDetailPanel(detail, charm, this.selectedVariant);
    detail.style.display = 'flex';
  }

  _updateDetailPanel(detail, charm, variant) {
    const img   = detail.querySelector('.charm-builder__detail-img');
    const name  = detail.querySelector('.charm-builder__detail-name');
    const price = detail.querySelector('.charm-builder__detail-price');

    if (img)   img.src = variant ? variant.image : charm.image;
    if (name)  name.textContent = charm.title;
    if (price) price.textContent = this._formatMoney(variant ? variant.price : charm.price);

    // Type badge and variant selector hidden — material auto-selected from chain option
    const badge = detail.querySelector('.charm-builder__detail-type-badge');
    if (badge) badge.style.display = 'none';

    const variantsEl = detail.querySelector('.charm-builder__detail-variants');
    if (variantsEl) variantsEl.innerHTML = '';
  }

  _hideDetail() {
    const detail = document.getElementById(`charm-detail-${this.sectionId}`);
    if (detail) detail.style.display = 'none';
    this.selectedCharm = null;
    this.selectedVariant = null;
  }

  // ─── GRID RENDER ─────────────────────────────────────────────────────────

  _renderGrid() {
    const grid = document.getElementById(`charm-grid-${this.sectionId}`);
    if (!grid) return;

    let visible = this.activeFilter !== 'all'
      ? this.charms.filter(c => c.category === this.activeFilter)
      : this.charms;

    if (visible.length === 0) {
      grid.innerHTML = '<p class="charm-builder__empty">No charms available.</p>';
      return;
    }

    grid.innerHTML = visible.map(charm => {
      const matVariant = this._getVariantForMaterial(charm, this.activeMaterial);
      const imgSrc     = matVariant ? matVariant.image : charm.image;
      return `
        <button
          class="charm-builder__charm-item"
          data-charm-id="${charm.productId}"
          data-charm-type="${charm.type}"
          aria-label="${charm.title}"
        >
          <img src="${imgSrc}" alt="${charm.title}" loading="lazy">
        </button>
      `;
    }).join('');

    grid.querySelectorAll('.charm-builder__charm-item:not([disabled])').forEach(item => {
      item.addEventListener('click', () => {
        const charm = this.charms.find(c => c.productId === parseInt(item.dataset.charmId, 10));
        if (!charm) return;
        // Auto-select the material-matched variant, then place immediately
        const matVariant = this._getVariantForMaterial(charm, this.activeMaterial);
        this.selectedVariant = matVariant || (charm.variants?.length > 0 ? charm.variants[0] : null);
        this.selectedCharm = charm;
        this._placeCharm(charm);
      });
    });
  }

  _isCharmUsed(productId) {
    return Object.values(this.slots).some(c => c?.productId === productId);
  }

  // ─── MATERIAL FILTERING ───────────────────────────────────────────────────

  // Returns the charm variant best matching the current material selection.
  // Matches by checking if variant title contains the material keyword (gold/silver).
  _getVariantForMaterial(charm, material) {
    if (!material || material === 'all' || !charm.variants?.length) return null;
    const kw = material.toLowerCase();
    return charm.variants.find(v => v.title.toLowerCase().includes(kw)) || null;
  }

  // Scan selected base product options for a material keyword.
  _detectMaterial() {
    for (const val of Object.values(this.selectedOptions)) {
      const lower = (val || '').toLowerCase();
      if (lower.includes('gold')) return 'gold';
      if (lower.includes('silver')) return 'silver';
    }
    return 'all';
  }

  // Apply material filter: update grid thumbnails + any already-placed charms.
  _applyMaterial(material) {
    if (this.activeMaterial === material) return;
    this.activeMaterial = material;
    this._renderGrid();

    // Update placed charms to their material-matched variant overlay
    Object.entries(this.slots).forEach(([slotNum, charm]) => {
      if (!charm) return;
      const matVariant = this._getVariantForMaterial(charm, material);
      if (matVariant) {
        const updated = { ...charm, id: matVariant.id, price: matVariant.price, image: matVariant.image, overlayImage: matVariant.overlayImage };
        this.slots[parseInt(slotNum, 10)] = updated;
        const overlay = this.el.querySelector(`.charm-builder__overlay-charm[data-slot="${slotNum}"]`);
        if (overlay) overlay.src = updated.overlayImage;
      }
    });

    this._updateCartBar();
  }

  // ─── CHAIN OVERLAY ───────────────────────────────────────────────────────

  _updateOverlay(slotNum, charm) {
    const overlay = this.el.querySelector(`.charm-builder__overlay-charm[data-slot="${slotNum}"]`);
    if (!overlay) return;

    overlay.src = charm.overlayImage;
    overlay.alt = charm.title;
    overlay.style.display = 'block';
    overlay.className = `charm-builder__overlay-charm charm-builder__overlay-charm--${charm.type}`;
    overlay.dataset.category = charm.category || '';
    // Per-product size from metafield (custom.charm_overlay_width). Falls back to CSS (category slider).
    overlay.style.width = charm.overlayWidth > 0 ? charm.overlayWidth + 'px' : '';

    overlay.classList.add('charm-entering');
    requestAnimationFrame(() => requestAnimationFrame(() => overlay.classList.remove('charm-entering')));

    this._repositionAllOverlays();
  }

  // ─── ARC MATH ────────────────────────────────────────────────────────────

  // Interpolate a point on the connector or hanging arc at fractional index t.
  _arcPosition(t, type) {
    const cfg = (type === 'hanging') ? this.hangingArc : this.connectorArc;
    if (!cfg.length) return { x: 50, y: this.baselineY };
    const lower = Math.max(0, Math.floor(t));
    const upper = Math.min(cfg.length - 1, Math.ceil(t));
    if (lower === upper) return { x: cfg[lower].x, y: cfg[lower].y };
    const frac = t - lower;
    return {
      x: cfg[lower].x + frac * (cfg[upper].x - cfg[lower].x),
      y: cfg[lower].y + frac * (cfg[upper].y - cfg[lower].y)
    };
  }

  // CSS rotation (degrees) for a charm at arc position t.
  // Charms follow the perpendicular to the arc tangent, leaning with the bracelet curve.
  _arcTangentAngle(t, type) {
    const arc = (type === 'hanging') ? this.hangingArc : this.connectorArc;
    const maxT = arc.length - 1;
    const eps  = 0.3;
    const p1 = this._arcPosition(Math.max(0, t - eps), type);
    const p2 = this._arcPosition(Math.min(maxT, t + eps), type);
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    // At center: tangent is horizontal (dy≈0), rotation = 0 (hang straight down)
    // Left side: tangent goes right+down → charm leans left (negative rotation)
    // Right side: tangent goes right+up → charm leans right (positive rotation)
    return -Math.atan2(dy, dx) * (180 / Math.PI);
  }

  // ─── REPOSITION ALL OVERLAYS ─────────────────────────────────────────────

  _repositionAllOverlays() {
    const filledSlots = Object.keys(this.slots).map(Number).sort((a, b) => a - b);
    const maxSlots    = this.numSlots;
    const arcLen      = this.connectorArc.length - 1;

    const moveHotspot = (slotNum, t) => {
      const pos = this._arcPosition(t, 'connector');
      const hs  = this.el.querySelector(`.charm-builder__hotspot[data-slot="${slotNum}"]`);
      if (hs) { hs.style.left = `${pos.x}%`; hs.style.top = `${pos.y}%`; }
    };

    const moveOverlay = (slotNum, t, charmType) => {
      const type    = charmType || 'connector';
      const pos     = this._arcPosition(t, type);
      const angle   = this._arcTangentAngle(t, type);
      const overlay = this.el.querySelector(`.charm-builder__overlay-charm[data-slot="${slotNum}"]`);
      if (overlay && overlay.style.display !== 'none') {
        overlay.style.left = `${pos.x}%`;
        overlay.style.top  = `${pos.y}%`;
        // Connector charms (rings/circles sitting ON the chain) don't tilt — they sit flat.
        // Hanging charms lean with the arc tangent angle.
        const rotAngle = type === 'connector' ? 0 : angle;
        const translate = type === 'hanging' ? 'translateX(-50%)' : 'translate(-50%, -50%)';
        overlay.style.transform = `${translate} rotate(${rotAngle}deg)`;
        overlay.style.transformOrigin = type === 'hanging' ? '50% 0%' : '50% 50%';
      }
    };

    if (filledSlots.length === 0) {
      Array.from({ length: maxSlots }, (_, i) => i + 1).forEach((slotNum, idx) => moveHotspot(slotNum, idx));
      return;
    }

    // Space charms evenly but cap the gap — fewer charms cluster near center,
    // 5 charms spread fully. Max spacing = 1.5 arc units between neighbours.
    const m = filledSlots.length;
    let tValues;
    if (m === 1) {
      tValues = [arcLen / 2];
    } else {
      const spacing     = Math.min(arcLen / (m - 1), 1.5);
      const totalSpread = spacing * (m - 1);
      const startT      = arcLen / 2 - totalSpread / 2;
      tValues = filledSlots.map((_, i) => startT + i * spacing);
    }

    filledSlots.forEach((slotNum, idx) => {
      const t = tValues[idx];
      moveHotspot(slotNum, t);
      moveOverlay(slotNum, t, this.slots[slotNum]?.type);
    });

    const emptySlots = Array.from({ length: maxSlots }, (_, i) => i + 1).filter(s => !this.slots[s]);
    emptySlots.forEach(slotNum => moveHotspot(slotNum, slotNum - 1));
  }

  // ─── CHARM SWING PHYSICS ─────────────────────────────────────────────────
  // Chain image stays still. Individual charm overlays swing like pendulums.
  // newSlot: the slot just placed/removed (bigger swing). Others: sympathetic sway.

  _jiggleChain(newSlot) {
    const overlays = [...this.el.querySelectorAll('.charm-builder__overlay-charm')]
      .filter(o => o.style.display !== 'none');

    overlays.forEach((overlay, i) => {
      const slotNum  = parseInt(overlay.dataset.slot, 10);
      const isNew    = slotNum === newSlot;
      const amplitude = isNew ? 18 : 4;   // new charm swings big, others sway lightly
      const duration  = isNew ? 1800 : 1200;
      const delay     = i * 40;
      const baseTransform = overlay.style.transform;
      let startTime = null;

      const animate = (now) => {
        if (!startTime) startTime = now + delay;
        const elapsed = now - startTime;
        if (elapsed < 0) { requestAnimationFrame(animate); return; }
        if (elapsed >= duration) { overlay.style.transform = baseTransform; return; }

        const t = elapsed / duration;
        // Pendulum: decaying sine — starts at full amplitude, settles to 0
        const swing = amplitude * Math.sin(t * Math.PI * 3.5) * Math.pow(1 - t, 1.1);
        overlay.style.transform = baseTransform + ` rotate(${swing}deg)`;
        requestAnimationFrame(animate);
      };

      requestAnimationFrame(animate);
    });
  }

  // ─── CART BAR ────────────────────────────────────────────────────────────

  _updateCartBar() {
    const count      = Object.keys(this.slots).length;
    const charmTotal = Object.values(this.slots).reduce((s, c) => s + (parseInt(c?.price, 10) || 0), 0);
    const total      = (parseInt(this.basePrice, 10) || 0) + charmTotal;

    const countEl    = this.el.querySelector('.charm-builder__cart-count');
    const totalEl    = this.el.querySelector('.charm-builder__cart-total');
    const topPriceEl = this.el.querySelector('.charm-builder__base-price');
    const atcBtn     = document.getElementById(`charm-builder-atc-${this.sectionId}`);

    if (countEl)    countEl.textContent    = `${count} charm${count !== 1 ? 's' : ''}`;
    if (totalEl)    totalEl.textContent    = this._formatMoney(total);
    if (topPriceEl) topPriceEl.textContent = this._formatMoney(total);
    if (atcBtn)     atcBtn.disabled        = count === 0;
  }

  // ─── ADD TO CART ─────────────────────────────────────────────────────────

  async _addToCart() {
    const btn = document.getElementById(`charm-builder-atc-${this.sectionId}`);
    if (!btn || btn.disabled) return;

    btn.disabled = true;
    btn.textContent = 'Adding…';

    const items = [
      { id: this.baseVariantId, quantity: 1 },
      ...Object.values(this.slots).filter(Boolean).map(c => ({ id: c.id, quantity: 1 }))
    ];

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) throw new Error(`${res.status}`);

      btn.textContent = 'Added!';
      btn.classList.add('is-added');

      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
      document.dispatchEvent(new CustomEvent('dispatch:cart-count-refresh', { bubbles: true }));
      document.querySelector('[data-open-cart], .js-cart-open, .js-mini-cart-trigger')?.click();

      setTimeout(() => {
        btn.textContent = 'Add to Bag';
        btn.disabled = false;
        btn.classList.remove('is-added');
      }, 3000);

    } catch (err) {
      console.error('[CharmBuilder] cart error:', err);
      btn.textContent = 'Error — try again';
      btn.disabled = false;
      setTimeout(() => { btn.textContent = 'Add to Bag'; }, 3000);
    }
  }

  // ─── HELPERS ─────────────────────────────────────────────────────────────

  _formatMoney(cents) {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: this.currencyCode,
      minimumFractionDigits: 2
    }).format(cents / 100);
  }

  // ─── EVENT BINDING ───────────────────────────────────────────────────────

  _bindEvents() {
    this.el.querySelectorAll('.charm-builder__slot-btn').forEach(btn =>
      btn.addEventListener('click', () => this._openSlot(parseInt(btn.dataset.slot, 10)))
    );

    this.el.querySelectorAll('.charm-builder__slot-remove').forEach(btn =>
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this._removeCharm(parseInt(btn.dataset.slot, 10));
      })
    );

    // Detail panel hidden — click-to-place is direct, no confirmation step needed

    document.getElementById(`charm-builder-atc-${this.sectionId}`)
      ?.addEventListener('click', () => this._addToCart());

    this._bindDragEvents();

    const baseVarUI = document.getElementById(`base-variants-ui-${this.sectionId}`);
    if (baseVarUI) {
      baseVarUI.querySelectorAll('.charm-builder__base-opt-btn.is-active').forEach(btn => {
        this.selectedOptions[parseInt(btn.dataset.optionIndex, 10)] = btn.dataset.optionValue;
      });
      this._resolveBaseVariant();

      baseVarUI.querySelectorAll('.charm-builder__base-opt-btn').forEach(btn => {
        btn.addEventListener('click', () => {
          const idx = parseInt(btn.dataset.optionIndex, 10);
          baseVarUI.querySelectorAll(`.charm-builder__base-opt-btn[data-option-index="${idx}"]`).forEach(b =>
            b.classList.toggle('is-active', b === btn)
          );
          this.selectedOptions[idx] = btn.dataset.optionValue;
          this._resolveBaseVariant();
        });
      });
    }
  }

  // ─── DRAG TO REORDER SLOTS (mouse + touch) ───────────────────────────────

  _bindDragEvents() {
    let dragSrc   = null;
    let touchGhost = null;

    const allSlots = () => this.el.querySelectorAll('.charm-builder__slot');

    const clearDragState = () => {
      allSlots().forEach(s => s.classList.remove('is-dragging', 'drag-over'));
      if (touchGhost) { touchGhost.remove(); touchGhost = null; }
      dragSrc = null;
    };

    const doSwap = (targetNum) => {
      if (dragSrc !== null && targetNum !== null && dragSrc !== targetNum) {
        this._swapSlots(dragSrc, targetNum);
      }
      clearDragState();
    };

    allSlots().forEach(slot => {
      const slotNum = parseInt(slot.dataset.slot, 10);

      // ── Mouse drag ──
      slot.addEventListener('dragstart', e => {
        if (!this.slots[slotNum]) { e.preventDefault(); return; }
        dragSrc = slotNum;
        slot.classList.add('is-dragging');
        e.dataTransfer.effectAllowed = 'move';
      });
      slot.addEventListener('dragend', clearDragState);
      slot.addEventListener('dragover', e => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragSrc !== null && dragSrc !== slotNum) slot.classList.add('drag-over');
      });
      slot.addEventListener('dragleave', () => slot.classList.remove('drag-over'));
      slot.addEventListener('drop', e => {
        e.preventDefault();
        slot.classList.remove('drag-over');
        doSwap(slotNum);
      });

      // ── Touch drag ──
      slot.addEventListener('touchstart', e => {
        if (!this.slots[slotNum]) return;
        dragSrc = slotNum;
        slot.classList.add('is-dragging');

        // Clone slot as floating ghost
        const rect = slot.getBoundingClientRect();
        touchGhost = slot.cloneNode(true);
        Object.assign(touchGhost.style, {
          position: 'fixed', zIndex: 1000, pointerEvents: 'none',
          opacity: '0.85', width: rect.width + 'px', height: rect.height + 'px',
          left: rect.left + 'px', top: rect.top + 'px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)', transition: 'none'
        });
        document.body.appendChild(touchGhost);
      }, { passive: true });

      slot.addEventListener('touchmove', e => {
        if (!touchGhost || dragSrc === null) return;
        const touch = e.touches[0];
        const w = touchGhost.offsetWidth, h = touchGhost.offsetHeight;
        touchGhost.style.left = (touch.clientX - w / 2) + 'px';
        touchGhost.style.top  = (touch.clientY - h / 2) + 'px';

        // Highlight slot under finger
        touchGhost.style.display = 'none';
        const under = document.elementFromPoint(touch.clientX, touch.clientY);
        touchGhost.style.display = '';
        allSlots().forEach(s => s.classList.remove('drag-over'));
        const target = under?.closest('.charm-builder__slot');
        if (target && parseInt(target.dataset.slot, 10) !== dragSrc) target.classList.add('drag-over');
      }, { passive: true });

      slot.addEventListener('touchend', e => {
        if (dragSrc === null) return;
        const touch = e.changedTouches[0];
        if (touchGhost) touchGhost.style.display = 'none';
        const under = document.elementFromPoint(touch.clientX, touch.clientY);
        if (touchGhost) touchGhost.style.display = '';
        const target = under?.closest('.charm-builder__slot');
        const targetNum = target ? parseInt(target.dataset.slot, 10) : null;
        doSwap(targetNum);
      });
    });

    this._refreshDraggable();
  }

  _refreshDraggable() {
    this.el.querySelectorAll('.charm-builder__slot').forEach(slot => {
      const slotNum  = parseInt(slot.dataset.slot, 10);
      const filled   = !!this.slots[slotNum];
      slot.setAttribute('draggable', filled ? 'true' : 'false');
      // Also set on the inner button so the grab cursor works everywhere
      const btn = slot.querySelector('.charm-builder__slot-btn');
      if (btn) btn.setAttribute('draggable', filled ? 'true' : 'false');
    });
  }

  _swapSlots(fromSlot, toSlot) {
    const fromCharm = this.slots[fromSlot] || null;
    const toCharm   = this.slots[toSlot]   || null;

    // Swap charm data
    if (fromCharm) this.slots[toSlot]   = fromCharm;
    else           delete this.slots[toSlot];
    if (toCharm)   this.slots[fromSlot] = toCharm;
    else           delete this.slots[fromSlot];

    // Re-render both slot UIs
    [fromSlot, toSlot].forEach(sNum => {
      const charm  = this.slots[sNum];
      const slotEl = this.el.querySelector(`.charm-builder__slot[data-slot="${sNum}"]`);
      if (!slotEl) return;
      const thumb = slotEl.querySelector('.charm-builder__slot-thumb');
      const plus  = slotEl.querySelector('.charm-builder__slot-plus');
      const rem   = slotEl.querySelector('.charm-builder__slot-remove');
      const btn   = slotEl.querySelector('.charm-builder__slot-btn');
      if (charm) {
        if (thumb) { thumb.src = charm.image; thumb.style.display = 'block'; }
        if (plus)  plus.style.display = 'none';
        if (rem)   rem.style.display  = 'flex';
        btn?.classList.add('is-filled');
      } else {
        if (thumb) { thumb.src = ''; thumb.style.display = 'none'; }
        if (plus)  plus.style.display = 'flex';
        if (rem)   rem.style.display  = 'none';
        btn?.classList.remove('is-filled');
      }
    });

    // Update overlays
    [fromSlot, toSlot].forEach(sNum => {
      const charm   = this.slots[sNum];
      const overlay = this.el.querySelector(`.charm-builder__overlay-charm[data-slot="${sNum}"]`);
      if (!overlay) return;
      if (charm) {
        overlay.src = charm.overlayImage;
        overlay.style.display = 'block';
        overlay.className = `charm-builder__overlay-charm charm-builder__overlay-charm--${charm.type}`;
      } else {
        overlay.src = '';
        overlay.style.display = 'none';
        overlay.className = 'charm-builder__overlay-charm';
        overlay.style.transform = '';
      }
    });

    this._refreshDraggable();
    this._repositionAllOverlays();
    this._updateCartBar();
    this._renderGrid();
  }

  _resolveBaseVariant() {
    if (!this.baseVariants.length) return;

    const match = this.baseVariants.find(v =>
      Object.entries(this.selectedOptions).every(([idx, val]) =>
        v[`option${parseInt(idx) + 1}`] === val
      )
    );

    if (match && match.available) {
      this.baseVariantId = match.id;
      this.basePrice     = match.price;
      this._updateCartBar();
    }

    // Detect material selection and update charm display
    this._applyMaterial(this._detectMaterial());
  }
}

// Bootstrap
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

function boot() {
  document.querySelectorAll('.charm-builder').forEach(el => new CharmBuilder(el));
}

// Shopify theme editor: re-init when section is reloaded after a setting change
document.addEventListener('shopify:section:load', (event) => {
  const el = event.target.querySelector('.charm-builder');
  if (el) new CharmBuilder(el);
});
